import { morphSection } from '@theme/section-renderer';

/**
 * Lamiano shipping protection.
 *
 * Auto-adds the `versandschutz` product to the cart whenever there are real
 * items in cart, unless the user has explicitly opted out via the toggle.
 * Opt-out state lives in `cart.attributes.sp_opted_out` so it survives page
 * reloads and is visible to Liquid (which renders the toggle in the right
 * state on every section morph).
 */

const SP_HANDLE = 'versandschutz';
const OPT_OUT_ATTR = 'sp_opted_out';

let busy = false;

const fetchJSON = (url, body) =>
  fetch(url, {
    method: body ? 'POST' : 'GET',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: body ? JSON.stringify(body) : undefined,
  }).then((r) => r.json());

const getToggleRoot = () => document.querySelector('[data-shipping-protection-toggle]');
const getSPVariantId = () => {
  if (window.LamianoSP && window.LamianoSP.variantId) return window.LamianoSP.variantId;
  const root = getToggleRoot();
  if (root && root.dataset.spVariantId) {
    const id = parseInt(root.dataset.spVariantId, 10);
    if (id) return id;
  }
  return null;
};

const getSectionIds = () => {
  const ids = new Set();
  const toggleRoot = getToggleRoot();
  if (toggleRoot?.dataset.spSectionId) ids.add(toggleRoot.dataset.spSectionId);
  document.querySelectorAll('cart-items-component[data-section-id]').forEach((el) => {
    ids.add(el.dataset.sectionId);
  });
  return Array.from(ids);
};

const isOptedOut = (cart) =>
  !!(cart && cart.attributes && cart.attributes[OPT_OUT_ATTR] === 'true');
const findSPItem = (cart) => cart.items.find((i) => i.handle === SP_HANDLE) || null;
const realItems = (cart) => cart.items.filter((i) => i.handle !== SP_HANDLE);

async function postWithSections(url, payload) {
  const sectionIds = getSectionIds();
  if (sectionIds.length) payload.sections = sectionIds;
  return fetchJSON(url, payload);
}

async function addSP() {
  const variantId = getSPVariantId();
  if (!variantId) return null;
  return postWithSections('/cart/add.js', { items: [{ id: variantId, quantity: 1 }] });
}

async function removeSP(itemKey) {
  return postWithSections('/cart/change.js', { id: itemKey, quantity: 0 });
}

/**
 * Single atomic update: set the opt-out attribute and remove SP in one call.
 * Used when the user toggles off.
 */
async function removeSPAndOptOut() {
  const variantId = getSPVariantId();
  if (!variantId) return null;
  return postWithSections('/cart/update.js', {
    updates: { [variantId]: 0 },
    attributes: { [OPT_OUT_ATTR]: 'true' },
  });
}

/**
 * Clear the opt-out attribute (used when the user toggles back on).
 */
async function clearOptOut() {
  return postWithSections('/cart/update.js', {
    attributes: { [OPT_OUT_ATTR]: '' },
  });
}

function applyResponseSections(response) {
  if (!response || !response.sections) return;
  for (const [sectionId, html] of Object.entries(response.sections)) {
    if (html) morphSection(sectionId, html);
  }
}

function setCheckboxChecked(value) {
  const cb = document.querySelector('#shipping-protection-toggle');
  if (cb) cb.checked = value;
}

async function reconcile() {
  if (busy) return;
  busy = true;
  try {
    if (!getSPVariantId()) return;

    const cart = await fetchJSON('/cart.js');
    const sp = findSPItem(cart);
    const real = realItems(cart);
    const optedOut = isOptedOut(cart);
    let response = null;
    let nextSpInCart = !!sp;

    if (real.length === 0 && sp) {
      response = await removeSP(sp.key);
      nextSpInCart = false;
    } else if (real.length > 0 && !sp && !optedOut) {
      response = await addSP();
      nextSpInCart = true;
    }

    if (response) applyResponseSections(response);
    setCheckboxChecked(nextSpInCart);
  } catch (e) {
    console.warn('[shipping-protection] reconcile failed', e);
  } finally {
    busy = false;
  }
}

async function onToggleChange(event) {
  const checked = event.target.checked;
  busy = true;
  try {
    const cart = await fetchJSON('/cart.js');
    const sp = findSPItem(cart);
    let response = null;

    if (!checked && sp) {
      // Opt-out: atomic remove SP + set attribute
      response = await removeSPAndOptOut();
    } else if (!checked && !sp) {
      // Already no SP, just persist the opt-out
      response = await postWithSections('/cart/update.js', {
        attributes: { [OPT_OUT_ATTR]: 'true' },
      });
    } else if (checked && !sp) {
      // Opt-in: clear attribute then add SP
      await clearOptOut();
      response = await addSP();
    } else if (checked && sp) {
      // Already has SP, just clear opt-out (defensive)
      response = await clearOptOut();
    }

    applyResponseSections(response);
    setCheckboxChecked(checked);
  } catch (e) {
    console.warn('[shipping-protection] toggle change failed', e);
  } finally {
    busy = false;
  }
}

function init() {
  reconcile();
  document.addEventListener('cart:update', reconcile);
  document.addEventListener('change', (e) => {
    if (e.target && e.target.matches('#shipping-protection-toggle')) onToggleChange(e);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}